import React, { useState } from "react";
import classes from "./Login.module.css";
import logo from "../../img/LoginImg/Logo.png";
import x from "../../img/LoginImg/x.svg";
import google from "../../img/LoginImg/google.png";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import { auth, db } from "../../firebase/firebase";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import WelcomeModal from "../WelcomeModal/WelcomeModal";

const loginErrorToHebrew = (code) => {
  switch (code) {
    case "auth/invalid-email":
      return "כתובת מייל לא תקינה";
    case "auth/user-disabled":
      return "החשבון מושבת. פנה לתמיכה.";
    case "auth/user-not-found":
    case "auth/invalid-credential":
    case "auth/wrong-password":
      return "מייל או סיסמה שגויים";
    case "auth/too-many-requests":
      return "יותר מדי ניסיונות. נסה שוב מאוחר יותר";
    case "auth/network-request-failed":
      return "בעיית רשת, נסה שוב";
    default:
      return "שגיאה בהתחברות, נסה שוב";
  }
};

const resetErrorToHebrew = (code) => {
  switch (code) {
    case "auth/invalid-email":
      return "כתובת מייל לא תקינה";
    case "auth/user-not-found":
      return "לא נמצא חשבון עם המייל הזה";
    case "auth/too-many-requests":
      return "יותר מדי ניסיונות. נסה שוב בעוד מספר דקות";
    case "auth/network-request-failed":
      return "בעיית רשת, נסה שוב";
    default:
      return "שגיאה באיפוס הסיסמה, נסה שוב";
  }
};

function Login({ onClose, onSwitchToSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeRequiresVerification, setWelcomeRequiresVerification] = useState(false);
  const [welcomeEmail, setWelcomeEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const handleResetPassword = async () => {
    setErrorMsg("");
    setInfoMsg("");
    if (!email.trim()) {
      setErrorMsg("נא להזין את כתובת המייל שלך קודם");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setInfoMsg("שלחנו קישור לאיפוס הסיסמה למייל. בדוק גם בתיקיית הספאם.");
    } catch (error) {
      console.error("Password reset failed:", error.message);
      setErrorMsg(resetErrorToHebrew(error?.code));
    }
  };

  const handleEmailLogin = async () => {
    if (submitting) return;
    setErrorMsg("");
    setInfoMsg("");
    if (!email.trim() || !password) {
      setErrorMsg("יש למלא מייל וסיסמה");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = result.user;
      await user.reload();

      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user);
        } catch (verificationError) {
          console.warn("sendEmailVerification on login failed:", verificationError);
        }

        setWelcomeEmail(user.email || email.trim());
        setWelcomeRequiresVerification(true);
        setShowWelcome(true);
        setTimeout(() => {
          signOut(auth).catch((signOutErr) => {
            console.error("signOut after unverified login failed:", signOutErr);
          });
        }, 0);
        return;
      }

      console.log("User logged in");
      onClose();
    } catch (error) {
      console.error("Login failed:", error.message);
      setErrorMsg(loginErrorToHebrew(error?.code));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setErrorMsg("");
    setInfoMsg("");
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // בדיקה אם המשתמש חדש - אם כן ניצור לו דוקומנט עם קרדיט חינם
      const userRef = doc(db, "users", user.uid);
      const existingUser = await getDoc(userRef);

      if (!existingUser.exists()) {
        console.log("➕ [Google Login] New user - creating document with 2 free credits");
        await setDoc(userRef, {
          email: user.email,
          currentPlan: "free",
          planStatus: "none",
          credits: {
            remaining: 2,  // 2 ניסיונות חינם
            lastUpdatedAt: serverTimestamp(),
          },
          entitlements: {
            canDownloadFull: false,
          },
          stats: {
            converted: 0,
            downloaded: 0,
            lastUpdated: serverTimestamp(),
          },
          auditVersion: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log("Google user:", result.user);
        setWelcomeRequiresVerification(false);
        setWelcomeEmail(user.email || "");
        setShowWelcome(true);
        return;
      }

      onClose();
    } catch (error) {
      console.error("Google login failed:", error.message);
      if (error?.code !== "auth/popup-closed-by-user" && error?.code !== "auth/cancelled-popup-request") {
        setErrorMsg("ההתחברות עם גוגל נכשלה. נסה שוב.");
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // מונע רענון דף
    handleEmailLogin();
  };

  return (
    <>
    <WelcomeModal
      isOpen={showWelcome}
      onClose={onClose}
      requiresVerification={welcomeRequiresVerification}
      email={welcomeEmail}
    />
    <div className={classes.modalContainer}>
      <div className={classes.modalContent}>
        <button onClick={onClose} className={classes.closeButton}>
          <img src={x} alt="סגור" />
        </button>
        <img src={logo} className={classes.logo} alt="Logo" />
        <h2 className={classes.modalTitle}>התחברות</h2>

        <button onClick={handleGoogleLogin} className={classes.googleLogin}>
          הצטרפות מהירה עם גוגל
          <img src={google} className={classes.googleIcon} alt="Google Icon" />
        </button>

        <p className={classes.separator}>או התחבר באמצעות מייל וסיסמא</p>

        <form autoComplete="on" onSubmit={handleSubmit}>
          <input
            type="email"
            name="email"
            placeholder="אימייל"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={classes.inputField}
            autoComplete="email"
          />

          <input
            type="password"
            name="password"
            placeholder="סיסמא"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={classes.inputField}
            autoComplete="current-password"
          />

          {errorMsg && (
            <div
              style={{
                background: "#FEE2E2",
                border: "1px solid #EF4444",
                borderRadius: 12,
                padding: "10px 14px",
                margin: "0 auto 12px",
                width: "80%",
                fontSize: 14,
                color: "#991B1B",
                direction: "rtl",
                textAlign: "right",
              }}
            >
              {errorMsg}
            </div>
          )}

          {infoMsg && !errorMsg && (
            <div
              style={{
                background: "#D1FAE5",
                border: "1px solid #10B981",
                borderRadius: 12,
                padding: "10px 14px",
                margin: "0 auto 12px",
                width: "80%",
                fontSize: 14,
                color: "#065F46",
                direction: "rtl",
                textAlign: "right",
              }}
            >
              {infoMsg}
            </div>
          )}

          <button type="submit" className={classes.submitButton} disabled={submitting}>
            {submitting ? "מתחבר..." : "התחבר"}
          </button>
        </form>

        <button
          className={classes.resetPasswordButton}
          onClick={handleResetPassword}
        >
          שכחת סיסמה? איפוס סיסמה
        </button>

        <button
          className={classes.switchToSignupButton}
          onClick={onSwitchToSignup}
        >
          עדיין אין לך חשבון? הירשם
        </button>
      </div>
    </div>
    </>
  );
}

export default Login;
